import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import CardListMobile from "../../templates/mobile/CardListMobile";
import { useIsMobile } from "../../hooks/useIsMobile";
import { db } from "../../config/firebase";

const COLLECTION_RECEITAS = "financeiro_receitas";
const COLLECTION_CONTAS_PAGAR = "financeiro_contas_pagar";

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function inRange(date, start, end) {
  if (!date || !start || !end) return false;
  return date >= start && date <= end;
}

function firstDayOfMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [y, m] = yyyyMm.split("-").map(Number);
  return new Date(y, m - 1, 1, 0, 0, 0, 0);
}

function lastDayOfMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [y, m] = yyyyMm.split("-").map(Number);
  return new Date(y, m, 0, 23, 59, 59, 999);
}

function statusUpper(value) {
  return String(value || "").trim().toUpperCase();
}

export default function FinanceiroFluxoCaixaPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const isMobile = useIsMobile(900);

  const now = new Date();
  const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [mesReferencia, setMesReferencia] = useState(mesAtual);
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const [snapReceitas, snapDespesas] = await Promise.all([
          getDocs(query(collection(db, COLLECTION_RECEITAS), where("grupoId", "==", usuario.grupoId))),
          getDocs(query(collection(db, COLLECTION_CONTAS_PAGAR), where("grupoId", "==", usuario.grupoId))),
        ]);

        let listaReceitas = snapReceitas.docs.map((d) => ({ id: d.id, ...d.data() }));
        let listaDespesas = snapDespesas.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (restringirEmpresa && empresaEscopoId) {
          const empresaRef = String(empresaEscopoId);
          listaReceitas = listaReceitas.filter((item) => String(item?.empresaId || "") === empresaRef);
          listaDespesas = listaDespesas.filter((item) => String(item?.empresaId || "") === empresaRef);
        }

        setReceitas(listaReceitas);
        setDespesas(listaDespesas);
      } catch (e) {
        console.error("Erro ao carregar fluxo de caixa:", e);
        setErro("Erro ao carregar dados financeiros para o fluxo de caixa.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, restringirEmpresa, empresaEscopoId]);

  const periodoInicio = useMemo(() => firstDayOfMonth(mesReferencia), [mesReferencia]);
  const periodoFim = useMemo(() => lastDayOfMonth(mesReferencia), [mesReferencia]);

  const resumo = useMemo(() => {
    const base = {
      entradasPrevistas: 0,
      entradasRealizadas: 0,
      saidasPrevistas: 0,
      saidasRealizadas: 0,
      receberEmAberto: 0,
      pagarEmAberto: 0,
    };

    for (const r of receitas) {
      const status = statusUpper(r.status);
      const valorPrevisto = Number(r.valorPrevisto || 0);
      const valorRecebido = Number(r.valorRecebido || 0);
      const desconto = Number(r.desconto || 0);
      const valorLiquidoPrevisto = valorPrevisto - desconto;

      const dataPrevista = parseDate(r.vencimento || r.competencia || r.createdAt);
      const dataRealizada = parseDate(r.dataRecebimento);

      const previstaNoMes = inRange(dataPrevista, periodoInicio, periodoFim);
      const realizadaNoMes = inRange(dataRealizada, periodoInicio, periodoFim);

      if (status !== "CANCELADO" && previstaNoMes) {
        base.entradasPrevistas += valorLiquidoPrevisto;
      }

      if (status === "RECEBIDO" && realizadaNoMes) {
        base.entradasRealizadas += valorRecebido || valorLiquidoPrevisto;
      }

      if ((status === "PREVISTO" || status === "ATRASADO") && previstaNoMes) {
        base.receberEmAberto += valorLiquidoPrevisto;
      }
    }

    for (const d of despesas) {
      const status = statusUpper(d.status);
      const valorFinal = Number(d.valorFinal || 0);
      const dataPrevista = parseDate(d.vencimento || d.competencia || d.createdAt);
      const dataRealizada = parseDate(d.dataPagamento);

      const previstaNoMes = inRange(dataPrevista, periodoInicio, periodoFim);
      const realizadaNoMes = inRange(dataRealizada, periodoInicio, periodoFim);

      if (status !== "CANCELADO" && previstaNoMes) {
        base.saidasPrevistas += valorFinal;
      }

      if (status === "PAGO" && realizadaNoMes) {
        base.saidasRealizadas += valorFinal;
      }

      if ((status === "PENDENTE" || status === "VENCIDO") && previstaNoMes) {
        base.pagarEmAberto += valorFinal;
      }
    }

    return {
      ...base,
      saldoPrevisto: base.entradasPrevistas - base.saidasPrevistas,
      saldoRealizado: base.entradasRealizadas - base.saidasRealizadas,
    };
  }, [receitas, despesas, periodoInicio, periodoFim]);

  const receitasMes = useMemo(() => {
    return receitas
      .filter((r) => inRange(parseDate(r.vencimento || r.competencia || r.createdAt), periodoInicio, periodoFim))
      .sort((a, b) => String(a.vencimento || "").localeCompare(String(b.vencimento || ""), "pt-BR"))
      .slice(0, 8);
  }, [receitas, periodoInicio, periodoFim]);

  const despesasMes = useMemo(() => {
    return despesas
      .filter((d) => inRange(parseDate(d.vencimento || d.competencia || d.createdAt), periodoInicio, periodoFim))
      .sort((a, b) => String(a.vencimento || "").localeCompare(String(b.vencimento || ""), "pt-BR"))
      .slice(0, 8);
  }, [despesas, periodoInicio, periodoFim]);

  const resumoCardsMobile = useMemo(
    () => [
      { id: "entradas-previstas", nome: "Entradas previstas", detalhes: [{ icone: "💰", label: "Valor", valor: `R$ ${formatMoney(resumo.entradasPrevistas)}` }] },
      { id: "saidas-previstas", nome: "Saídas previstas", detalhes: [{ icone: "💸", label: "Valor", valor: `R$ ${formatMoney(resumo.saidasPrevistas)}` }] },
      { id: "saldo-previsto", nome: "Saldo previsto", detalhes: [{ icone: "📊", label: "Valor", valor: `R$ ${formatMoney(resumo.saldoPrevisto)}` }] },
      { id: "entradas-realizadas", nome: "Entradas realizadas", detalhes: [{ icone: "✅", label: "Valor", valor: `R$ ${formatMoney(resumo.entradasRealizadas)}` }] },
      { id: "saidas-realizadas", nome: "Saídas realizadas", detalhes: [{ icone: "🧾", label: "Valor", valor: `R$ ${formatMoney(resumo.saidasRealizadas)}` }] },
      { id: "saldo-realizado", nome: "Saldo realizado", detalhes: [{ icone: "🏁", label: "Valor", valor: `R$ ${formatMoney(resumo.saldoRealizado)}` }] },
      { id: "receber", nome: "A receber no mês", detalhes: [{ icone: "📥", label: "Valor", valor: `R$ ${formatMoney(resumo.receberEmAberto)}` }] },
      { id: "pagar", nome: "A pagar no mês", detalhes: [{ icone: "📤", label: "Valor", valor: `R$ ${formatMoney(resumo.pagarEmAberto)}` }] },
    ],
    [resumo]
  );

  const receitasCardsMobile = useMemo(
    () => receitasMes.map((item) => ({
      id: `rec-${item.id}`,
      nome: item.descricao || "Receita",
      subtitulo: item.clienteOrigem || item.clienteNome || "-",
      status: String(item.status || "").toLowerCase(),
      detalhes: [
        { icone: "📅", label: "Vencimento", valor: item.vencimento || "-" },
        { icone: "💰", label: "Valor", valor: `R$ ${formatMoney((Number(item.valorPrevisto || 0) - Number(item.desconto || 0)))}` },
      ],
    })),
    [receitasMes]
  );

  const despesasCardsMobile = useMemo(
    () => despesasMes.map((item) => ({
      id: `desp-${item.id}`,
      nome: item.descricao || "Despesa",
      subtitulo: item.fornecedor || "-",
      status: String(item.status || "").toLowerCase(),
      detalhes: [
        { icone: "📅", label: "Vencimento", valor: item.vencimento || "-" },
        { icone: "💸", label: "Valor", valor: `R$ ${formatMoney(item.valorFinal || 0)}` },
      ],
    })),
    [despesasMes]
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        titulo="Fluxo de Caixa"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
      >
        <section className="pc-card" style={{ marginBottom: 12 }}>
          <h3 style={{ marginTop: 0 }}>Fluxo de Caixa</h3>
          <p style={{ marginTop: 8, opacity: 0.9 }}>Consolidação mensal de receitas e contas a pagar.</p>
          <div style={{ marginTop: 12 }}>
            <label className="pc-field" style={{ marginBottom: 0 }}>
              <span>Mês de referência</span>
              <input
                type="month"
                className="pc-input"
                value={mesReferencia}
                onChange={(e) => setMesReferencia(e.target.value)}
              />
            </label>
          </div>
        </section>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

        <CardListMobile
          items={resumoCardsMobile}
          carregando={carregando}
          erro={erro}
          titulo="Resumo financeiro"
          searchPlaceholder="Buscar indicador..."
          emptyMessage="Sem indicadores para exibir."
          hideDefaultActions
          getSearchText={(item) => `${item.nome} ${item?.detalhes?.map((d) => d.valor).join(" ") || ""}`}
        />

        <CardListMobile
          items={receitasCardsMobile}
          carregando={carregando}
          erro={erro}
          titulo="Receitas do mês"
          searchPlaceholder="Buscar receita..."
          emptyMessage="Sem receitas no período."
          hideDefaultActions
          getSearchText={(item) => `${item.nome} ${item.subtitulo} ${item?.detalhes?.map((d) => d.valor).join(" ") || ""}`}
        />

        <CardListMobile
          items={despesasCardsMobile}
          carregando={carregando}
          erro={erro}
          titulo="Contas a pagar do mês"
          searchPlaceholder="Buscar despesa..."
          emptyMessage="Sem contas a pagar no período."
          hideDefaultActions
          getSearchText={(item) => `${item.nome} ${item.subtitulo} ${item?.detalhes?.map((d) => d.valor).join(" ") || ""}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Financeiro" subtitulo="Gerenciar / Fluxo de Caixa">
      <section className="pc-card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Fluxo de Caixa (Previsto x Realizado)</h3>
        <p style={{ marginTop: 8, opacity: 0.9 }}>
          Consolidação mensal das coleções de receitas e contas a pagar.
        </p>

        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label className="pc-field" style={{ marginBottom: 0 }}>
            <span>Mês de referência</span>
            <input
              type="month"
              className="pc-input"
              value={mesReferencia}
              onChange={(e) => setMesReferencia(e.target.value)}
              style={{ minWidth: 180 }}
            />
          </label>
        </div>
      </section>

      {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

      <section className="pc-card" style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
          }}
        >
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Entradas previstas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.entradasPrevistas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Saídas previstas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.saidasPrevistas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Saldo previsto</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.saldoPrevisto)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Entradas realizadas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.entradasRealizadas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Saídas realizadas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.saidasRealizadas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Saldo realizado</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.saldoRealizado)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>A receber no mês</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.receberEmAberto)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>A pagar no mês</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(resumo.pagarEmAberto)}</div>
          </div>
        </div>
      </section>

      <section className="pc-card pc-table-card" style={{ marginBottom: 14 }}>
        <header className="pc-table-header">
          <div>
            <h3>Receitas do mês</h3>
            <p>Itens previstos para o período selecionado.</p>
          </div>
        </header>

        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Cliente</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16 }}>Carregando...</td>
                </tr>
              ) : receitasMes.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16 }}>Sem receitas no período.</td>
                </tr>
              ) : (
                receitasMes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.descricao || "-"}</td>
                    <td>{item.clienteOrigem || item.clienteNome || "-"}</td>
                    <td>{item.vencimento || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>R$ {formatMoney((Number(item.valorPrevisto || 0) - Number(item.desconto || 0)))}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div>
            <h3>Contas a pagar do mês</h3>
            <p>Despesas previstas para o período selecionado.</p>
          </div>
        </header>

        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Fornecedor</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16 }}>Carregando...</td>
                </tr>
              ) : despesasMes.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 16 }}>Sem contas a pagar no período.</td>
                </tr>
              ) : (
                despesasMes.map((item) => (
                  <tr key={item.id}>
                    <td>{item.descricao || "-"}</td>
                    <td>{item.fornecedor || "-"}</td>
                    <td>{item.vencimento || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>R$ {formatMoney(item.valorFinal || 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
